import { useEffect, useState } from "react";
import {
  Lazy,
  lazyCat,
  lazySelected,
  lazySelectedCat,
  Active,
  activeCat,
  activeSelected,
  activeSelectedCat,
  VeryActive,
  veryActiveCat,
  veryActiveSelected,
  veryActiveSelectedCat,
  thin,
  thinCatUrl,
  thinSelected,
  thinSelectedCat,
  normal,
  normalCat,
  normalSelected,
  normalSelectedCat,
  fat,
  fatCat,
  fatSelected,
  fatSelectedCat
} from "../../assets/svg";
import { useTranslation } from "react-i18next";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

function PetBodyInfo({ petInfo, setPetInfo, setIsComplete, petType }) {
  // Initialize from petInfo if available (for navigation back)
  const [petKg, setPetKg] = useState(petInfo.kg || '');
  const [petGr, setPetGr] = useState(petInfo.gr || 0);
  const [activityLevel, setActivityLevel] = useState(petInfo.activity || null);
  const [bodyType, setBodyType] = useState(petInfo.bodyType || null);
  const [activator, setActivator] = useState(false);

  const { t } = useTranslation();

  const petActivity = [
    {
      id: 1,
      title: t("sedentary"),
      value: "lazy",
      svg: petType === 'dog' ? Lazy : lazyCat,
      desc: petType === 'cat' ? t("catBasicNeeds") : t("basicNeeds"),
      selectedSvg: petType === 'dog' ? lazySelected : lazySelectedCat,
    },
    {
      id: 2,
      title: t("active"),
      value: "active",
      svg: petType === 'dog' ? Active : activeCat,
      desc: petType === 'cat' ? t("catDailyWalks") : t("dailyWalks"),
      selectedSvg: petType === 'dog' ? activeSelected : activeSelectedCat,
    },
    {
      id: 3,
      title: t("veryActive"),
      value: "very_active",
      svg: petType === 'dog' ? VeryActive : veryActiveCat,
      desc: petType === 'cat' ? t("catRegularExercise") : t("regularExercise"),
      selectedSvg: petType === 'dog' ? veryActiveSelected : veryActiveSelectedCat,
    },
  ];

  const petBodyType = [
    {
      id: 1,
      title: t("thin"),
      value: "thin",
      svg: petType === 'dog' ? thin : thinCatUrl,
      selectedSvg: petType === 'dog' ? thinSelected : thinSelectedCat,
    },
    {
      id: 2,
      title: t("ideal"),
      value: "normal",
      svg: petType === 'dog' ? normal : normalCat,
      selectedSvg: petType === 'dog' ? normalSelected : normalSelectedCat,
    },
    {
      id: 3,
      title: t("overweight"),
      value: "fat",
      svg: petType === 'dog' ? fat : fatCat,
      selectedSvg: petType === 'dog' ? fatSelected : fatSelectedCat,
    },
  ];

  const increasePetKg = () => {
    if (petInfo.kg < 100) {
      setPetInfo({ ...petInfo, kg: Number(petInfo.kg) + 1 });
    }
  };

  const decreasePetKg = () => {
    if (petInfo.kg > 0) {
      setPetInfo({
        ...petInfo,
        kg: Number(petInfo.kg) - 1,
      });
    }
  };

  useEffect(() => {
    setActivator(true);
    if (activator) {
      setPetInfo({
        ...petInfo,
        kg: petKg,
      });
    }
  }, [petKg]);

  useEffect(() => {
    setActivator(true);
    if (activator) {
      setPetInfo({
        ...petInfo,
        gr: petGr,
      });
    }
  }, [petGr]);

  useEffect(() => {
    setActivator(true);
    if (activator) {
      setPetInfo({
        ...petInfo,
        activity: activityLevel,
      });
    }
  }, [activityLevel]);

  useEffect(() => {
    setActivator(true);
    if (activator) {
      setPetInfo({
        ...petInfo,
        bodyType: bodyType,
      });
    }
  }, [bodyType]);

  useEffect(() => {
    petInfo.kg && petInfo.activity && petInfo.bodyType
      ? setIsComplete(true)
      : setIsComplete(false);
  }, [petInfo]);

  return (
    <div className="flex flex-col justify-center items-center w-full max-w-2xl px-3 sm:px-0 mb-6 overflow-hidden">
      <div className="mb-6 border-b-2 border-yellow-400 pb-4 w-full flex flex-col items-center">
        <div className="w-full flex flex-col items-center">
          <div className="block font-medium font-poppins text-lg text-yellow-600 text-center">
            {petType == 'dog' ? t("petWeight") : t("catPetWeight")}
          </div>

          <div className="mt-4 flex items-center justify-center">
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              value={petInfo.kg || ''}
              placeholder=""
              onKeyPress={(e) => !/[0-9]/.test(e.key) && e.preventDefault()}
              onChange={(e) => {
                const val = e.target.value === '' ? '' : Number(e.target.value);
                setPetKg(val);
              }}
              className="py-2 px-3 w-16 text-lg text-yellow-700 text-center bg-white border-2 border-yellow-400 rounded-lg appearance-none focus:outline-none focus:ring-2 focus:ring-kito-green focus:border-kito-green font-bold"
            />
            <span className="text-lg text-yellow-600 ml-2 font-medium">kg</span>
          </div>
        </div>
      </div>

      <div className="mt-5 border-b-2 border-yellow-400 pb-4">
        <label className="text-lg font-poppins font-medium text-yellow-600">
          {petType == 'dog' ? t("petBodyShape") : t("catPetBodyShape")}
        </label>

        <fieldset className="mt-5 w-full">
          <div className="flex items-stretch justify-center gap-2 sm:gap-6 w-full">
            {petBodyType.map((type) => (
              <div
                key={type.id}
                className={`flex flex-col items-center justify-start cursor-pointer p-3 rounded-lg border transition-all flex-1 min-w-0 max-w-[8rem] min-h-[9rem]
                  ${type.value === petInfo.bodyType
                    ? "border-kito-green bg-green-50"
                    : "border-gray-200 bg-white hover:border-gray-300"
                  }`}
                onClick={() => {
                  setBodyType(
                    type.value === petInfo.bodyType ? "" : type.value
                  );
                }}
              >
                <div className="flex justify-center items-center h-16 w-16 mb-3">
                  <img
                    className="h-full w-full object-contain"
                    src={type.value === petInfo.bodyType ? type.selectedSvg : type.svg}
                    alt={type.title}
                    style={{ filter: 'brightness(0.6) saturate(1.5) contrast(1.2)' }}
                  />
                </div>
                <button
                  type="button"
                  className={`inline-flex items-center justify-center w-full px-2 py-1.5 border shadow-sm text-[11px] sm:text-xs font-medium rounded-md truncate
                  ${type.value === petInfo.bodyType
                      ? "bg-kito-green border-transparent text-white hover:bg-opacity-90"
                      : "bg-white border-gray-300 text-gray-700 hover:bg-gray-50"
                    }`}
                >
                  {type.title}
                </button>
              </div>
            ))}
          </div>
        </fieldset>
      </div>
      <div className="mt-6">
        <label className="font-poppins text-lg font-medium text-yellow-600 ">
          {petType == 'dog' ? t("petActivity") : t("catPetActivity")}
        </label>

        <fieldset className="mt-4 w-full">
          <div className="flex items-stretch justify-center gap-2 sm:gap-5 w-full">
            {petActivity.map((activity) => (
              <div
                key={activity.id}
                className={`flex flex-col items-center justify-start cursor-pointer p-3 rounded-lg border transition-all flex-1 min-w-0 max-w-[8rem] min-h-[11rem]
                  ${activity.value === petInfo.activity
                    ? "border-kito-green bg-green-50"
                    : "border-gray-200 bg-white hover:border-gray-300"
                  }`}
                onClick={() => {
                  setActivityLevel(
                    activity.value === petInfo.activity ? "" : activity.value
                  );
                }}
              >
                <div className="flex justify-center items-center h-16 w-16 mb-3">
                  <img
                    className="h-full w-full object-contain"
                    src={activity.value === petInfo.activity ? activity.selectedSvg : activity.svg}
                    alt={activity.title}
                    style={{ filter: 'brightness(0.6) saturate(1.5) contrast(1.2)' }}
                  />
                </div>

                <button
                  type="button"
                  className={`inline-flex items-center justify-center w-full px-2 py-1.5 border shadow-sm text-[11px] sm:text-xs font-medium rounded-md truncate
                    ${activity.value === petInfo.activity
                      ? "bg-kito-green border-transparent text-white hover:bg-opacity-90"
                      : "bg-white border-gray-300 text-gray-700 hover:bg-gray-50"
                    }`}
                >
                  {activity.title}
                </button>
                <div className="mt-2 text-[10px] sm:text-xs text-gray-500 text-center leading-tight px-1">
                  {activity.desc}
                </div>
              </div>
            ))}
          </div>
        </fieldset>
      </div>
    </div>
  );
}

export default PetBodyInfo;
